import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  CurrencyDollarIcon, 
  UsersIcon, 
  ClockIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  PhoneIcon,
  EyeIcon,
  XCircleIcon,
  StarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Simple Chart Components
const PieChart = ({ data, colors, size = 120 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = 0;
  
  const radius = size / 2 - 10;
  const centerX = size / 2;
  const centerY = size / 2;
  
  return (
    <div className="flex items-center space-x-4">
      <svg width={size} height={size} className="transform -rotate-90">
        {data.map((item, index) => {
          if (item.value === 0) return null;
          
          const percentage = (item.value / total) * 100;
          const angle = (item.value / total) * 360;
          const startAngle = currentAngle;
          const endAngle = currentAngle + angle;
          
          const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
          const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
          const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
          const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);
          
          const largeArcFlag = angle > 180 ? 1 : 0;
          
          const pathData = [
            `M ${centerX} ${centerY}`,
            `L ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            'Z'
          ].join(' ');
          
          currentAngle += angle;
          
          return (
            <path
              key={index}
              d={pathData}
              fill={colors[index % colors.length]}
              className="hover:opacity-80 transition-opacity cursor-pointer"
            />
          );
        })}
      </svg>
      
      <div className="space-y-1">
        {data.map((item, index) => (
          <div key={index} className="flex items-center space-x-2 text-xs">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: colors[index % colors.length] }}
            />
            <span className="text-gray-700">{item.label}: {item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const BarChart = ({ data, colors, height = 100 }) => {
  const maxValue = Math.max(...data.map(item => item.value));
  
  return (
    <div className="space-y-2">
      {data.map((item, index) => (
        <div key={index} className="flex items-center space-x-3">
          <div className="w-20 text-xs text-gray-600 truncate">{item.label}</div>
          <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
            <div
              className="h-4 rounded-full transition-all duration-300"
              style={{
                width: `${(item.value / maxValue) * 100}%`,
                backgroundColor: colors[index % colors.length]
              }}
            />
            <span className="absolute right-2 top-0 text-xs text-white font-medium leading-4">
              {item.value}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

const LineChart = ({ data, color = '#3B82F6', height = 80 }) => {
  const maxValue = Math.max(...data.map(item => item.value));
  const minValue = Math.min(...data.map(item => item.value));
  const range = maxValue - minValue || 1;
  
  const points = data.map((item, index) => {
    const x = (index / (data.length - 1)) * 200;
    const y = height - ((item.value - minValue) / range) * height;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <div className="space-y-2">
      <svg width="200" height={height + 20} className="w-full">
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2"
          points={points}
          className="drop-shadow-sm"
        />
        {data.map((item, index) => {
          const x = (index / (data.length - 1)) * 200;
          const y = height - ((item.value - minValue) / range) * height;
          return (
            <circle
              key={index}
              cx={x}
              cy={y}
              r="3"
              fill={color}
              className="hover:r-4 transition-all cursor-pointer"
            />
          );
        })}
      </svg>
      <div className="flex justify-between text-xs text-gray-500">
        {data.map((item, index) => (
          <span key={index}>{item.label}</span>
        ))}
      </div>
    </div>
  );
};

const AdvancedLeadStats = ({ leads, onFilterChange, activeFilter }) => {
  const [stats, setStats] = useState({});
  const [timeRange, setTimeRange] = useState('all'); // all, today, week, month
  const [leadStatuses, setLeadStatuses] = useState([]);

  useEffect(() => {
    console.log('🚀 AdvancedLeadStats component mounted, fetching statuses...');
    fetchLeadStatuses();
  }, []);

  useEffect(() => {
    console.log('📊 Leads or statuses changed. Leads:', leads.length, 'Statuses:', leadStatuses.length);
    if (leadStatuses.length > 0) {
      console.log('✅ Calculating stats with statuses...');
      calculateStats();
    } else {
      console.log('⚠️ Waiting for statuses to load before calculating stats...');
    }
  }, [leads, timeRange, leadStatuses]);

  const fetchLeadStatuses = async () => {
    try {
      console.log('📊 Fetching lead statuses for analytics...');
      const { leadsApi } = await import('../config/api');
      const response = await leadsApi.get('/lead-statuses');
      console.log('📊 Lead statuses API response:', response.data);
      if (response.data.success) {
        console.log('✅ Lead statuses loaded:', response.data.statuses.length, 'statuses');
        console.log('📋 Statuses with outcomes:', response.data.statuses.map(s => ({ 
          name: s.name, 
          label: s.label, 
          outcome: s.outcome 
        })));
        setLeadStatuses(response.data.statuses);
      } else {
        console.error('❌ Failed to load lead statuses:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching lead statuses:', error);
    }
  };

  const getStatusOutcome = (statusName) => {
    // Case-insensitive matching
    const status = leadStatuses.find(s => s.name.toLowerCase() === statusName.toLowerCase());
    if (!status) {
      console.log(`❌ Status "${statusName}" NOT FOUND. Available statuses:`, leadStatuses.map(s => s.name));
      // Check for common typos
      if (statusName.toLowerCase() === 'succesful') {
        console.log('⚠️ Found typo "succesful", treating as "successful" (converted)');
        return 'converted';
      }
    } else {
      console.log(`✅ Found status "${statusName}": outcome = "${status.outcome}"`);
    }
    return status?.outcome || '';
  };

  const calculateStats = () => {
    const now = new Date();
    let filteredLeads = leads;
    
    // Debug logging
    console.log('📊 ===== ANALYTICS CALCULATION START =====');
    console.log('All leads:', leads);
    console.log('Sample lead structure:', leads[0]);
    console.log('Lead statuses loaded:', leadStatuses);
    console.log('Lead statuses with outcomes:', leadStatuses.map(s => ({ name: s.name, label: s.label, outcome: s.outcome })));

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    filteredLeads = filteredLeads.filter(lead => {
      const createdAt = new Date(lead.createdAt);
      switch (timeRange) {
        case 'today':
          return createdAt >= today;
        case 'week':
          return createdAt >= weekAgo;
        case 'month':
          return createdAt >= monthAgo;
        default:
          return true;
      }
    });

    const statusCounts = filteredLeads.reduce((acc, lead) => {
      acc[lead.status] = (acc[lead.status] || 0) + 1;
      return acc;
    }, {});

    const sourceCounts = filteredLeads.reduce((acc, lead) => {
      acc[lead.source] = (acc[lead.source] || 0) + 1;
      return acc;
    }, {});

    // Calculate won/lost based on outcome field
    const wonLeads = filteredLeads.filter(lead => {
      const outcome = getStatusOutcome(lead.status);
      console.log(`Lead "${lead.name}" (status: ${lead.status}) -> outcome: ${outcome}`);
      return outcome === 'converted';
    });
    const lostLeads = filteredLeads.filter(lead => getStatusOutcome(lead.status) === 'rejected');
    const activeLeads = filteredLeads.filter(lead => {
      const outcome = getStatusOutcome(lead.status);
      return outcome !== 'converted' && outcome !== 'rejected';
    });

    console.log('✅ Won leads:', wonLeads.length, wonLeads.map(l => l.name));
    console.log('❌ Lost leads:', lostLeads.length, lostLeads.map(l => l.name));
    console.log('🔄 Active leads:', activeLeads.length);

    // Calculate budget values with proper number conversion
    const activeBudget = activeLeads.reduce((sum, lead) => {
      const budget = parseFloat(lead.budget || lead.value) || 0;
      return sum + budget;
    }, 0);
    
    const lostBudget = lostLeads.reduce((sum, lead) => {
      const budget = parseFloat(lead.budget || lead.value) || 0;
      console.log(`💰 Lost lead "${lead.name}": budget=${lead.budget}, value=${lead.value}, parsed=${budget}`);
      return sum + budget;
    }, 0);
    
    const wonValue = wonLeads.reduce((sum, lead) => {
      const budget = parseFloat(lead.budget || lead.value) || 0;
      console.log(`💰 Won lead "${lead.name}": budget=${lead.budget}, value=${lead.value}, parsed=${budget}`);
      return sum + budget;
    }, 0);
    
    console.log('💵 Active budget calculated:', activeBudget);
    console.log('💵 Lost budget calculated:', lostBudget);
    console.log('💵 Won budget calculated:', wonValue);
    console.log('📊 Won leads count:', wonLeads.length);
    console.log('📊 Lost leads count:', lostLeads.length);

    const conversionRate = filteredLeads.length > 0 
      ? ((wonLeads.length / filteredLeads.length) * 100).toFixed(1)
      : 0;

    // Prepare chart data - group by outcome
    const statusChartData = [];
    
    // Add active statuses
    leadStatuses
      .filter(s => !s.outcome || s.outcome === '')
      .forEach(status => {
        const count = statusCounts[status.name] || 0;
        if (count > 0) {
          statusChartData.push({ label: status.label, value: count });
        }
      });
    
    // Add won/lost
    if (wonLeads.length > 0) {
      statusChartData.push({ label: 'Won ✅', value: wonLeads.length });
    }
    if (lostLeads.length > 0) {
      statusChartData.push({ label: 'Lost ❌', value: lostLeads.length });
    }

    const sourceChartData = Object.entries(sourceCounts).map(([source, count]) => ({
      label: source.charAt(0).toUpperCase() + source.slice(1).replace('_', ' '),
      value: count
    }));

    // Monthly trend data (last 6 months)
    const monthlyData = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const monthLeads = filteredLeads.filter(lead => {
        const leadDate = new Date(lead.createdAt);
        return leadDate >= monthStart && leadDate <= monthEnd;
      });
      
      monthlyData.push({
        label: date.toLocaleDateString('en-US', { month: 'short' }),
        value: monthLeads.length
      });
    }

    setStats({
      total: filteredLeads.length,
      new: statusCounts.new || 0,
      contacted: statusCounts.contacted || 0,
      qualified: statusCounts.qualified || 0,
      proposal: statusCounts.proposal || 0,
      negotiation: statusCounts.negotiation || 0,
      closed_won: wonLeads.length,
      closed_lost: lostLeads.length,
      on_hold: statusCounts.on_hold || 0,
      totalValue: activeBudget,
      lostValue: lostBudget,
      wonValue,
      conversionRate,
      sourceCounts,
      statusChartData,
      sourceChartData,
      monthlyData
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const StatCard = ({ title, value, icon: Icon, color, onClick, isActive, subtitle }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-700 border-blue-200',
      green: 'bg-green-50 text-green-700 border-green-200',
      purple: 'bg-purple-50 text-purple-700 border-purple-200',
      orange: 'bg-orange-50 text-orange-700 border-orange-200',
      red: 'bg-red-50 text-red-700 border-red-200',
      yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    };

    const activeClasses = isActive ? 'ring-1 ring-blue-500 bg-blue-100' : '';

    return (
      <div 
        className={`${colorClasses[color]} ${activeClasses} border rounded-md p-2 cursor-pointer hover:shadow-sm transition-all duration-150 hover:scale-[1.02]`}
        onClick={onClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium opacity-70 mb-0.5 truncate">{title}</p>
            <p className="text-sm font-bold leading-tight">{value}</p>
            {subtitle && <p className="text-xs opacity-60 mt-0.5 truncate">{subtitle}</p>}
          </div>
          {Icon && <Icon className="h-4 w-4 opacity-50 flex-shrink-0 ml-1" />}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">📊 Lead Analytics</h2>
          <p className="text-gray-600 text-sm">Track your lead performance and conversion</p>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">Time Range:</label>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>
        </div>
      </div>

      {/* Main Stats      {/* Key Metrics Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">🎯 Key Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <StatCard
            title="Total Leads"
            value={stats.total || 0}
            icon={UsersIcon}
            color="blue"
            onClick={() => {
              onFilterChange('');
              setTimeout(() => {
                const target = document.getElementById('analytics-leads-table-section');
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            isActive={activeFilter === '' || activeFilter === 'all'}
          />
          
          <StatCard
            title="New Leads"
            value={stats.new || 0}
            icon={StarIcon}
            color="green"
            onClick={() => {
              onFilterChange('new');
              setTimeout(() => {
                const target = document.getElementById('analytics-leads-table-section');
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            isActive={activeFilter === 'new'}
          />
          
          <StatCard
            title="Won 🏆"
            value={stats.closed_won || 0}
            icon={CheckCircleIcon}
            color="green"
            onClick={() => {
              onFilterChange('closed_won');
              setTimeout(() => {
                const target = document.getElementById('analytics-leads-table-section');
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            isActive={activeFilter === 'closed_won'}
            subtitle={formatCurrency(stats.wonValue || 0)}
          />
          
          <StatCard
            title="Lost ❌"
            value={stats.closed_lost || 0}
            icon={XCircleIcon}
            color="red"
            onClick={() => {
              onFilterChange('closed_lost');
              setTimeout(() => {
                const target = document.getElementById('analytics-leads-table-section');
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            isActive={activeFilter === 'closed_lost'}
            subtitle={formatCurrency(stats.lostValue || 0)}
          />
          
          <StatCard
            title="Conversion Rate"
            value={`${stats.conversionRate}%`}
            icon={ArrowTrendingUpIcon}
            color="orange"
            subtitle={`${stats.closed_won || 0} won out of ${stats.total || 0}`}
          />
        </div>
      </div>

      {/* Revenue Analytics */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">💰 Revenue Analytics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <StatCard
            title="Active Budget"
            value={formatCurrency(stats.totalValue || 0)}
            icon={CurrencyDollarIcon}
            color="blue"
            subtitle="Excluding lost leads"
          />
          
          <StatCard
            title="Won Budget"
            value={formatCurrency(stats.wonValue || 0)}
            icon={CheckCircleIcon}
            color="green"
            subtitle="Closed deal budgets"
          />
          
          <StatCard
            title="Lost Budget"
            value={formatCurrency(stats.lostValue || 0)}
            icon={ExclamationTriangleIcon}
            color="red"
            subtitle="Lost deal budgets"
          />
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Lead Status Pie Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">📊 Lead Status Distribution</h3>
          {stats.statusChartData && stats.statusChartData.length > 0 ? (
            <PieChart
              data={stats.statusChartData}
              colors={['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4', '#6B7280']}
              size={140}
            />
          ) : (
            <div className="text-center text-gray-500 py-8">No data available</div>
          )}
        </div>

        {/* Lead Sources Bar Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">📈 Lead Sources</h3>
          {stats.sourceChartData && stats.sourceChartData.length > 0 ? (
            <BarChart
              data={stats.sourceChartData}
              colors={['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16']}
            />
          ) : (
            <div className="text-center text-gray-500 py-8">No data available</div>
          )}
        </div>
      </div>

      {/* Monthly Trends Line Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">📉 Monthly Trends (Last 6 Months)</h3>
        {stats.monthlyData && stats.monthlyData.length > 0 ? (
          <LineChart
            data={stats.monthlyData}
            color="#3B82F6"
            height={100}
          />
        ) : (
          <div className="text-center text-gray-500 py-8">No data available</div>
        )}
      </div>

      {/* Interactive Lead Sources Grid */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1.5">
        <h3 className="text-xs font-medium text-gray-700 mb-1.5">🎯 Sources</h3>
        <div className="grid grid-cols-4 md:grid-cols-8 lg:grid-cols-10 gap-0.5">
          {Object.entries(stats.sourceCounts || {}).map(([source, count]) => (
            <div
              key={source}
              className="bg-gray-50 rounded-sm p-1 text-center cursor-pointer hover:bg-blue-50 transition-colors border border-gray-100 hover:border-blue-200 min-h-[50px] flex flex-col justify-center"
              onClick={() => {
                onFilterChange(`source:${source}`);
                setTimeout(() => {
                  const target = document.getElementById('analytics-leads-table-section');
                  if (target) {
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              }}
            >
              <div className="text-xs mb-0.5">
                {source === 'website' && '🌐'}
                {source === 'instagram' && '📷'}
                {source === 'facebook' && '📘'}
                {source === 'whatsapp' && '💬'}
                {source === 'linkedin' && '💼'}
                {source === 'referral' && '👥'}
                {source === 'email_campaign' && '📧'}
                {source === 'phone_call' && '📞'}
                {!['website', 'instagram', 'facebook', 'whatsapp', 'linkedin', 'referral', 'email_campaign', 'phone_call'].includes(source) && '📍'}
              </div>
              <p className="text-xs font-medium text-gray-700 capitalize truncate leading-none">{source.replace('_', ' ')}</p>
              <p className="text-xs font-bold text-blue-600 leading-none">{count}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdvancedLeadStats;
